/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { ACCOUNT_DATA_SERVICE, AccountDataService } from '../../../page-modules/account/services/account-data.service';
import { Generate2FASecret } from '../../../page-modules/account/store/account.actions';

@Component({
  selector: 'ptl-two-factor-setup',
  templateUrl: './two-factor-setup.component.html',
  styleUrls: ['./two-factor-setup.component.scss'],
})
export class TwoFactorSetupComponent implements OnInit {
  @Input() showLogoutButton = false;
  @Output() setupCompleted = new EventEmitter<void>();
  @Output() logoutRequested = new EventEmitter<void>();

  is2FASetupInProgress: boolean;
  is2FAErrorShown: boolean;
  twoFactorForm: FormGroup;
  qrCode: string;
  encodedSecret: string;

  constructor(
    private fb: FormBuilder,
    private store: Store,
    @Inject(ACCOUNT_DATA_SERVICE) private accountService: AccountDataService,
  ) {
    this.twoFactorForm = fb.group({
      verificationCode: ['', Validators.compose([Validators.required, Validators.pattern(/^[0-9]{6}$/)])],
    });
  }

  ngOnInit() {
    this.startSetup();
  }

  startSetup() {
    if (!this.is2FASetupInProgress) {
      this.is2FASetupInProgress = true;

      this.store.dispatch(new Generate2FASecret()).subscribe((state) => {
        this.accountService.generate2FASecret().subscribe(({ isSuccess, value, error }) => {
          this.is2FASetupInProgress = false;

          if (isSuccess && value) {
            this.qrCode = `data:image/png;base64,${value.totpSecretQRCode}`;
            this.encodedSecret = value.encodedTotpSecret;
            this.is2FAErrorShown = false;
          } else {
            this.is2FAErrorShown = true;
          }
        });
      });
    }
  }

  submit2FA() {
    if (this.twoFactorForm.valid && !this.is2FASetupInProgress) {
      this.is2FASetupInProgress = true;
      this.is2FAErrorShown = false;

      const request = {
        totpInitialCode: this.twoFactorForm.value.verificationCode,
        encodedTotpSecret: this.encodedSecret,
        overwrite: false,
      };

      this.accountService.submit2FA(request).subscribe(({ isSuccess, error }) => {
        this.is2FASetupInProgress = false;

        if (isSuccess) {
          this.twoFactorForm.reset();
          this.is2FAErrorShown = false;
          this.setupCompleted.emit();
        } else {
          this.is2FAErrorShown = true;
        }
      });
    }
  }

  regenerateQRCode() {
    this.qrCode = undefined;
    this.encodedSecret = undefined;
    this.twoFactorForm.reset();
    this.is2FAErrorShown = false;
    this.startSetup();
  }

  requestLogout() {
    this.logoutRequested.emit();
  }
}
